// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useState, useCallback } from 'react';
import { useVaccineClientStore } from '../../store/vaccineClientStore';
import { useDecisionTreeExport } from './hooks/useDecisionTreeExport';
import { useDecisionTreeOperations } from './hooks/useDecisionTreeOperations';
import { useDecisionTreeComputation } from '../../hooks/useDecisionTreeComputation';
import { DecisionTreeBuilderView } from './DecisionTreeBuilderView';
import { DecisionTreeResults, DecisionTreeResultsOverlay } from './components/DecisionTreeResults';
import { fromLegacy } from '../../lib/treeConversion';
import type { LegacyDecisionTree } from '../../lib/tree';
import type { ValidationIssue } from '../../lib/validation';
import { useAlerts } from '../../hooks/useAlerts';
import './DecisionTreeStyles.css';

interface DecisionTreeBuilderContainerProps {
  className?: string;
}

/**
 * Container component for DecisionTreeBuilder
 * Handles Zustand store access, side effects, and business logic
 * Passes clean props to presentational DecisionTreeBuilderView
 */
const DecisionTreeBuilderContainer: React.FC<DecisionTreeBuilderContainerProps> = ({ className }) => {
  const store = useVaccineClientStore();
  const {
    nodes,
    edges,
    rootId,
    selectedId,
    view,
    issues,
    showValidationDetails,
    showHelp,
    setSelectedId,
    setView,
    setShowValidationDetails,
    setShowHelp,
    validate,
    getTreeState
  } = store;

  // Split pane state
  const [leftPaneWidth, setLeftPaneWidth] = useState(() => {
    const saved = sessionStorage.getItem('easyhta:leftPaneWidth');
    return saved ? Number(saved) : 400;
  });

  // Use extracted hooks
  const { exportPNG } = useDecisionTreeExport(getTreeState, view);
  const {
    createBlank,
    loadTree,
    saveTree,
    convertToLegacyTree,
    structureVersion,
    legacyTree
  } = useDecisionTreeOperations(store);
  
  // Backend computation hook
  const { compute, loading: computeLoading, error: computeError, results: computeResults, lastComputedAt } = useDecisionTreeComputation();

  // Alert system for user-facing notifications
  const { showError, showWarning } = useAlerts();

  // Results modal state
  const [showResults, setShowResults] = useState(false);

  // Show results modal when computation completes
  useEffect(() => {
    if (computeResults && lastComputedAt) {
      setShowResults(true);
    }
  }, [computeResults, lastComputedAt]);

  // Show error notification
  useEffect(() => {
    if (computeError) {
      // Keep raw error in console for support; user sees friendly copy.
      console.error('Decision tree computation failed:', computeError);
      showError('Computation failed. Please check your inputs and try again.', 'Computation Failed');
    }
  }, [computeError, showError]);

  // Validate tree whenever it changes
  useEffect(() => {
    validate();
  }, [nodes, edges, rootId, validate]);

  // Handle body overflow when entering/exiting fullscreen
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (view.fullscreen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [view.fullscreen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const isEditable = (el: EventTarget | null) =>
      !!(el as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"]');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditable(event.target)) return;
      if (event.key === 'Escape') {
        if (view.fullscreen) {
          setView({ fullscreen: false });
        } else if (showValidationDetails) {
          setShowValidationDetails(false);
        } else if (showHelp) {
          setShowHelp(false);
        }
      } else if ((event.key === 'F' || event.key === 'f') && !event.ctrlKey && !event.metaKey) {
        const next = !view.fullscreen;
        setView({ fullscreen: next });
      } else if ((event.key === '?' || (event.key === '/' && event.shiftKey)) && !event.ctrlKey && !event.metaKey) {
        setShowHelp(!showHelp);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [view.fullscreen, showValidationDetails, showHelp, setView, setShowValidationDetails, setShowHelp]);

  const handleIssueClick = useCallback((issue: ValidationIssue) => {
    if (issue.nodeId) {
      setSelectedId(issue.nodeId);
      // TODO: Implement jump to node in SVG and expand in outline
    }
  }, [setSelectedId]);

  const handleEditorTreeChange = useCallback((newTree: LegacyDecisionTree) => {
    // Convert legacy tree format to new format
    // fromLegacy normalizes transition data from child nodes to edges
    const { nodes: newNodes, edges: newEdges } = fromLegacy(newTree.root);
    
    // Get current state directly from store
    const currentNodes = store.nodes;
    const currentEdges = store.edges;
    
    // Update existing nodes or add new ones
    Object.values(newNodes).forEach(node => {
      if (currentNodes[node.id]) {
        store.updateNode(node.id, node);
      } else {
        store.addNode(node);
      }
    });
    
    // Sync edges - updateEdge merges all properties (including transition_cost/transition_utility_loss)
    const existingEdgeKeys = new Set(
      currentEdges.map((e: any) => `${e.parentId}:${e.childId}`)
    );
    
    newEdges.forEach((edge: any) => {
      const edgeKey = `${edge.parentId}:${edge.childId}`;
      if (!existingEdgeKeys.has(edgeKey)) {
        store.addEdge(edge);
      } else {
        // updateEdge merges all edge properties, preserving transition data
        store.updateEdge(edge.parentId, edge.childId, edge);
      }
    });
  }, [store]);

  return (
    <>
      <DecisionTreeBuilderView
        className={className}
        isFullscreen={view.fullscreen}
        toolbarProps={{
          onCreateBlank: createBlank,
          onLoadTree: loadTree,
          onSaveTree: saveTree,
          onExportPNG: exportPNG,
          onToggleFullscreen: () => {
            const next = !view.fullscreen;
            setView({ fullscreen: next });
          },
          onShowHelp: () => setShowHelp(true),
          onCompute: () => {
            validate();
            const currentIssues = useVaccineClientStore.getState().issues;
            const hasErrors = currentIssues.some(i => i.severity === 'error');
            
            if (hasErrors) {
              showWarning('Cannot compute: Please fix validation errors first.');
              setShowValidationDetails(true);
              return;
            }
            compute();
          },
          computeLoading,
          computeError,
          view,
          onViewChange: setView,
          issues,
          onShowValidationDetails: () => setShowValidationDetails(true),
        }}
        splitPaneProps={{
          leftPaneWidth,
          onLeftPaneWidthChange: setLeftPaneWidth,
          editorTree: convertToLegacyTree(),
          editorSelectedNodeId: selectedId,
          onEditorTreeChange: handleEditorTreeChange,
          onEditorNodeSelect: setSelectedId,
          rendererTree: legacyTree,
          rendererStructureVersion: structureVersion,
          rendererSelectedNodeId: selectedId,
          onRendererNodeSelect: setSelectedId,
          rendererShowProbabilities: view.showProb,
          rendererShowCosts: view.showCost,
          rendererShowQALYs: view.showQaly,
          rendererShowTransitionDetails: view.showTransition,
          rendererNodeSize: view.nodeSize.toUpperCase() as 'S' | 'M' | 'L',
          rendererIssues: issues,
          rendererEdges: edges,
        }}
        modalsProps={{
          showValidationDetails,
          onCloseValidationDetails: () => setShowValidationDetails(false),
          issues,
          onIssueClick: handleIssueClick,
          showHelp,
          onCloseHelp: () => setShowHelp(false),
        }}
      />
      
      {/* Results Modal */}
      {showResults && computeResults && lastComputedAt && (
        <>
          <DecisionTreeResultsOverlay onClick={() => setShowResults(false)} />
          <DecisionTreeResults
            results={computeResults}
            computedAt={lastComputedAt}
            onClose={() => setShowResults(false)}
          />
        </>
      )}
    </>
  );
};

export default DecisionTreeBuilderContainer;

